import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import Footer from '../components/Footer';
import { sb } from '../supabase';
import RequestQuoteModal from '../components/factory/RequestQuoteModal';
import ProductGallery from '../components/product/ProductGallery';
import SellerStrip from '../components/product/SellerStrip';
import SpecRows from '../components/product/SpecRows';
import ProductChips from '../components/ProductChips';
import NegotiablePill from '../components/NegotiablePill';
import MoreOptionsBadge from '../components/MoreOptionsBadge';
import { buildProductRef } from '../lib/factoryChat';
import { getFactoryProductImages } from '../lib/productMedia';
import { formatCatalogPrice } from '../lib/displayCurrency';
import { productPageCopy, moqText, priceFromText } from '../lib/productPageCopy';
import { TrustStrip, ClampText } from '../components/product/ProductPageParts';
import AdminFeatureStar from '../components/product/AdminFeatureStar';

const T = {
  ar: {
    back: 'رجوع إلى المصنع', byFactory: 'المصنع', maker: 'الصانع', verifiedByMaabar: 'موثّق من معبر', ref: 'رمز المنتج', zoom: 'اضغط للتكبير', prev: 'السابق', next: 'التالي',
    msgStarting: 'جارٍ الفتح…',
    notFound: 'المنتج غير موجود', loading: '…',
    specsLabel: 'المواصفات', descLabel: 'الوصف', customLabel: 'خيارات التخصيص', moqLabel: 'الحد الأدنى للطلب',
    note: 'المصنع يرد بالسعر — لا حاجة لإدخال ميزانية.',
  },
  en: {
    back: 'Back to factory', byFactory: 'Factory', maker: 'Maker', verifiedByMaabar: 'Verified by Maabar', ref: 'Ref', zoom: 'Click to zoom', prev: 'Previous', next: 'Next',
    msgStarting: 'Opening…',
    notFound: 'Product not found', loading: '…',
    specsLabel: 'Specifications', descLabel: 'Description', customLabel: 'Customization options', moqLabel: 'MOQ',
    note: 'The factory responds with pricing — no budget needed.',
  },
  zh: {
    back: '返回工厂', byFactory: '工厂', maker: '制造商', verifiedByMaabar: '经 Maabar 认证', ref: '货号', zoom: '点击放大', prev: '上一个', next: '下一个',
    msgStarting: '正在打开…',
    notFound: '未找到产品', loading: '…',
    specsLabel: '规格', descLabel: '描述', customLabel: '定制选项', moqLabel: '起订量',
    note: '工厂会回复价格——无需填写预算。',
  },
};

// Standalone factory catalog product page.
//   /factory/:factoryId/product/:productId
// Gallery + lightbox + product details, then a single CTA that opens the shared
// RequestQuoteModal (product pre-attached → factory_product_id set).
//
// Info column order (owner, 2026-10-03, same as the app and ProductDetail):
// name, price, MOQ → trust strip → details (filled rows only) → description
// (2 lines + اقرأ المزيد) → factory strip → the two CTAs. A field the PDF
// import left empty is simply not shown — it used to read "contact the
// supplier for details", once per missing field.
export default function FactoryProductDetail({ lang = 'ar', user, profile, displayCurrency, exchangeRates }) {
  const { factoryId, productId } = useParams();
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const arc = isAr ? ' ar' : '';
  const c = T[lang] || T.ar;
  const pc = productPageCopy(lang);
  usePageTitle('suppliers', lang);

  const [factory, setFactory] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Owned by ProductGallery; mirrored here only so the arrow-key product walk
  // stands down while the fullscreen viewer has the arrows.
  const [lightbox, setLightbox] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqMode, setReqMode] = useState('quote'); // 'quote' | 'inquiry'
  const [msgBusy, setMsgBusy] = useState(false);

  // Direct chat with the factory (quick questions before a formal request).
  // Unified `messages` stack: there is no thread row to create first. A
  // buyer→factory message is addressed with receiver_id NULL + factory_id, and
  // resolves to the owner via factory_directory.linked_supplier_id, so opening
  // the chat is pure navigation. The product card rides along in route state and
  // Chat.jsx attaches it to the first message (Chat.jsx:128, :428).
  function handleMessageFactory() {
    if (!user) { nav('/login'); return; }
    if (msgBusy) return;
    setMsgBusy(true);
    nav(`/chat/f/${factoryId}`, { state: { product: buildProductRef(product) } });
  }

  // ── Walk the catalog product-to-product from the detail view ──
  // Hybrid context: the grid passes the exact ordered/filtered list it showed
  // (route state); on a deep link (no state) fall back to the factory's full
  // catalog order so the arrows always work.
  const loc = useLocation();
  const [siblings, setSiblings] = useState(() => (Array.isArray(loc.state?.siblings) ? loc.state.siblings : null));
  useEffect(() => {
    if (siblings) return undefined;
    let cancelled = false;
    (async () => {
      const { data } = await sb.from('factory_products')
        .select('id, factory_id, sort_order, created_at')
        .eq('factory_id', factoryId)
        .order('sort_order', { ascending: true }).order('created_at', { ascending: true });
      if (!cancelled) setSiblings((data || []).map((r) => ({ id: r.id, fid: r.factory_id })));
    })();
    return () => { cancelled = true; };
  }, [siblings, factoryId]);

  const navIdx = siblings ? siblings.findIndex((sx) => String(sx.id) === String(productId)) : -1;
  const prevSib = navIdx > 0 ? siblings[navIdx - 1] : null;
  const nextSib = siblings && navIdx >= 0 && navIdx < siblings.length - 1 ? siblings[navIdx + 1] : null;
  const goToSibling = (sx) => { if (sx) nav(`/factory/${sx.fid}/product/${sx.id}`, { state: { siblings } }); };

  // Keyboard arrows move between PRODUCTS — but not while the image lightbox or a
  // modal owns the arrows, or while typing. RTL mirrors direction.
  useEffect(() => {
    const onKey = (e) => {
      if (lightbox || reqOpen) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToSibling(isAr ? nextSib : prevSib); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToSibling(isAr ? prevSib : nextSib); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, reqOpen, prevSib, nextSib, isAr, siblings]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [{ data: p }, { data: f }] = await Promise.all([
        sb.from('factory_products').select('*').eq('id', productId).maybeSingle(),
        sb.from('factory_directory_public').select('*').eq('id', factoryId).maybeSingle(),
      ]);
      if (cancelled) return;
      // Guard mismatched URLs: the product must belong to this factory.
      setProduct(p && String(p.factory_id) === String(factoryId) ? p : null);
      setFactory(f || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [factoryId, productId]);

  if (loading) {
    return <div className="full-page"><div className="fx-wrap"><p style={{ color: 'var(--text-secondary)' }}>{c.loading}</p></div></div>;
  }
  if (!product || !factory) {
    return (
      <div className="full-page" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="fx-wrap">
          <button className={`fx-back${arc}`} onClick={() => nav(`/factory/${factoryId}`)}>{isAr ? '→ ' : '← '}{c.back}</button>
          <p style={{ color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{c.notFound}</p>
        </div>
        <Footer lang={lang} />
      </div>
    );
  }

  const productName = (isAr ? product.name_ar : lang === 'zh' ? product.name_zh : product.name_en)
    || product.name_en || product.name_ar || product.name_zh || '';
  const factoryName = (factory.company_name_latin || '').trim() || factory.company_name || '';
  const factoryLoc = [factory.city, factory.country].filter(Boolean).join(isAr ? '، ' : ', ');
  const gallery = getFactoryProductImages(product);

  // Display fields (ar/en, fall back to the other language). A missing value is
  // not shown at all.
  const specs = (isAr ? product.specifications_ar : product.specifications_en) || product.specifications_en || product.specifications_ar || '';
  const descr = (isAr ? product.description_ar : product.description_en) || product.description_en || product.description_ar || '';
  const custom = Array.isArray(product.customization_options) ? product.customization_options : [];
  const moqLine = moqText(product.moq, lang);
  // Catalog price (verbatim). Append the currency label unless the price text
  // already carries it. null → the page falls back to "On request".
  const priceText = formatCatalogPrice(product, isAr, exchangeRates);   // SAR only, every language, admin rate
  const navBtn = (disabled) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px',
    borderRadius: 'var(--radius-control)', border: '1px solid var(--border-strong)', background: 'transparent',
    color: disabled ? 'var(--text-muted)' : 'var(--text-primary)', fontSize: 12.5, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' });

  return (
    <div className="full-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fx-wrap">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <button className={`fx-back${arc}`} onClick={() => nav(`/factory/${factoryId}`)}>{isAr ? '→ ' : '← '}{c.back}</button>
          {siblings && siblings.length > 1 && navIdx >= 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={() => goToSibling(prevSib)} disabled={!prevSib} style={navBtn(!prevSib)}>
                {isAr ? `${c.prev} →` : `← ${c.prev}`}
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{navIdx + 1} / {siblings.length}</span>
              <button type="button" onClick={() => goToSibling(nextSib)} disabled={!nextSib} style={navBtn(!nextSib)}>
                {isAr ? `← ${c.next}` : `${c.next} →`}
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 8 }}>
          {/* ── Gallery ── */}
          <div style={{ flex: '1 1 380px', minWidth: 280 }}>
            <ProductGallery
              images={gallery}
              alt={productName}
              zoomTitle={c.zoom}
              onLightboxChange={setLightbox}
            />
          </div>

          {/* ── Info + request ── */}
          <div style={{ flex: '1 1 360px', minWidth: 280 }}>
            <AdminFeatureStar profile={profile} source="factory" productId={product.id} lang={lang} />
            {productName && <h1 className={`fx-h1${arc}`} style={{ fontSize: isAr ? 24 : 28, marginBottom: 10 }}>{productName}</h1>}

            {/* Price + MOQ right under the name. The catalog price when the
                factory printed one; otherwise pricing stays quote-based. */}
            <p className={`fx-card-meta${arc}`} style={{ margin: '0 0 4px', fontSize: 18, color: 'var(--text-primary)', fontWeight: 500 }}>
              {priceFromText(priceText, lang) || pc.priceOnRequest}
            </p>
            {priceText && <div style={{ margin: '6px 0 0' }}><NegotiablePill lang={lang} long /></div>}
            {moqLine && <p className={`fx-card-meta${arc}`} style={{ margin: '6px 0 0', fontSize: 13 }}>{moqLine}</p>}
            {product.also_count > 0 && <div style={{ margin: '8px 0 0' }}><MoreOptionsBadge count={product.also_count} lang={lang} /></div>}
            <div style={{ height: 14 }} />

            <TrustStrip lang={lang} verified={!!factory.is_verified} />

            <ProductChips product={product} factory={factory} lang={lang} style={{ margin: '0 0 16px' }} />

            {/* Details, then description — filled rows only (emptyMode="omit"). */}
            <SpecRows
              lang={lang}
              emptyMode="omit"
              rows={[
                { key: 'specs', label: c.specsLabel, value: specs },
                ...(custom.length > 0 ? [{
                  key: 'custom',
                  label: c.customLabel,
                  children: (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {custom.map((x, i) => {
                        const label = (isAr ? x.ar : x.en) || x.en || x.ar || '';
                        return label ? (
                          <span key={i} style={{ padding: '3px 11px', borderRadius: 20, background: 'var(--bg-hero)', fontSize: 12, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{label}</span>
                        ) : null;
                      })}
                    </div>
                  ),
                }] : []),
                { key: 'ref', label: c.ref, value: product.ref_code },
                ...(descr ? [{
                  key: 'desc',
                  label: c.descLabel,
                  children: <ClampText lang={lang} text={descr} style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} />,
                }] : []),
              ]}
            />

            {/* ── The factory as a named, vetted manufacturer ── */}
            <SellerStrip
              to={`/factory/${factoryId}`}
              image={factory.profile_image}
              kicker={c.maker}
              name={factoryName}
              meta={factoryLoc}
              verified={factory.is_verified}
              verifiedLabel={c.verifiedByMaabar}
              ctaLabel={pc.seeFactory}
              lang={lang}
            />
            <div style={{ height: 6 }} />

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className={`fx-btn-primary${arc}`} onClick={() => { setReqMode('quote'); setReqOpen(true); }}>{pc.requestQuote}</button>
              <button className={`fx-btn-ghost${arc}`} onClick={handleMessageFactory} disabled={msgBusy}>{msgBusy ? c.msgStarting : pc.askFactory}</button>
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '10px 0 0', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{c.note}</p>
          </div>
        </div>
      </div>

      {reqOpen && (
        <RequestQuoteModal
          lang={lang}
          user={user}
          factory={factory}
          product={product}
          mode={reqMode}
          displayCurrency={displayCurrency}
          onClose={() => setReqOpen(false)}
        />
      )}

      <Footer lang={lang} />
    </div>
  );
}
